/**
 * useCalendarDragDrop Hook
 *
 * 處理日曆視圖中 TimeBox 和 Event 的拖拽和縮放操作
 * - 拖動移動：改變開始/結束時間，保持持續時間不變
 * - 支援跨日期拖動（Week View）
 * - 拉伸調整：從頂部或底部邊緣調整持續時間
 * - 以 15 分鐘為單位對齊
 * 
 * 注意：此 Hook 負責拖拽交互，樂觀更新由上層組件（如 Calendar.tsx）實現
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { ScheduleItem } from '../../../types';
import { logger } from '../../../utils/logger';
import {
  DragState,
  DragOperation,
  calculateNewTimesWithDate,
  calculateTargetDayIndex,
  getDragOperation,
  getDragCursor,
  DISPLAY_CONFIG,
} from '../../../utils/calendarHelper';

export interface RescheduleRequest {
  itemId: string;
  itemType: 'timebox' | 'event';
  newStartTime: Date;
  newEndTime: Date;
}

interface UseCalendarDragDropOptions {
  hourHeight?: number;
  startHour?: number;
  endHour?: number;
  timeColumnWidth?: number;
  /** 日期列數組（用於週視圖跨日期拖動） */
  days?: Date[];
  onReschedule?: (request: RescheduleRequest) => Promise<void>;
  enabled?: boolean;
}

interface UseCalendarDragDropReturn {
  // 狀態
  dragState: DragState | null;
  isDragging: boolean;
  /** 是否處於長按狀態（用於視覺提起效果） */
  isLongPressing: boolean;

  // 滑鼠事件處理器
  handleMouseDown: (
    e: React.MouseEvent,
    item: ScheduleItem,
    containerRect: DOMRect,
    dayIndex?: number
  ) => void;
  handleMouseMove: (e: MouseEvent) => void;
  handleMouseUp: (e: MouseEvent) => void;

  // 觸控事件處理器（行動版）
  handleTouchStart: (
    e: React.TouchEvent,
    item: ScheduleItem,
    containerRect: DOMRect,
    dayIndex?: number
  ) => void;
  handleTouchMove: (e: TouchEvent) => void;
  handleTouchEnd: (e: TouchEvent) => void;

  // 工具函數
  getDragPreviewStyle: (item: ScheduleItem, dayIndex?: number) => React.CSSProperties | null;
  getCursorStyle: (mouseY: number, elementHeight: number) => string;
  /** 獲取當前拖拽的目標日期索引 */
  getCurrentDayIndex: () => number | undefined;

  // 清理
  resetDragState: () => void;
}

export const useCalendarDragDrop = (
  options: UseCalendarDragDropOptions = {}
): UseCalendarDragDropReturn => {
  const {
    hourHeight = DISPLAY_CONFIG.HOUR_HEIGHT,
    startHour = DISPLAY_CONFIG.START_HOUR,
    endHour = DISPLAY_CONFIG.END_HOUR,
    timeColumnWidth = DISPLAY_CONFIG.TIME_COLUMN_WIDTH,
    days,
    onReschedule,
    enabled = true,
  } = options;

  const [dragState, setDragState] = useState<DragState | null>(null);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const [isWaitingForLongPress, setIsWaitingForLongPress] = useState(false); // 追蹤長按等待期間
  const containerRectRef = useRef<DOMRect | null>(null);
  const hasMoved = useRef(false);
  const isTouchDrag = useRef(false);

  // 長按相關
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const isDragActivated = useRef(false);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const pendingDragData = useRef<{
    item: ScheduleItem;
    containerRect: DOMRect;
    dayIndex?: number;
    touch: { clientX: number; clientY: number };
  } | null>(null);

  // 長按配置
  const LONG_PRESS_DELAY = 600; // 長按門檻（ms）
  const SCROLL_CANCEL_THRESHOLD = 10; // 滾動取消門檻（px）

  // 清理長按計時器
  const clearLongPressTimer = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // 長按激活拖動
  const activateDrag = useCallback(() => {
    if (!pendingDragData.current) return;

    const { item, containerRect, dayIndex, touch } = pendingDragData.current;

    // 震動反饋（如果支援）
    if ('vibrate' in navigator) {
      navigator.vibrate(20);
    }

    // 設置視覺狀態
    setIsLongPressing(true);
    isDragActivated.current = true;

    // 設置拖動狀態
    containerRectRef.current = containerRect;
    hasMoved.current = false;
    isTouchDrag.current = true;

    setDragState({
      isDragging: true,
      operation: 'move',
      itemId: item.id,
      itemType: item.itemType,
      startY: touch.clientY,
      startX: touch.clientX,
      startMouseY: touch.clientY - containerRect.top,
      startMouseX: touch.clientX - containerRect.left,
      originalStartTime: new Date(item.startTime),
      originalEndTime: new Date(item.endTime!),
      currentStartTime: new Date(item.startTime),
      currentEndTime: new Date(item.endTime!),
      originalDayIndex: dayIndex,
      currentDayIndex: dayIndex,
    });
  }, []);

  // 開始拖動（滑鼠）
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, item: ScheduleItem, containerRect: DOMRect, dayIndex?: number) => {
      if (!enabled) return;

      // 阻止默認行為和事件冒泡
      e.preventDefault();
      e.stopPropagation();

      const element = e.currentTarget as HTMLElement;
      const elementRect = element.getBoundingClientRect();
      const mouseYInElement = e.clientY - elementRect.top;
      const elementHeight = elementRect.height;

      // 判斷操作類型
      const operation: DragOperation = getDragOperation(mouseYInElement, elementHeight);

      // 保存容器 rect
      containerRectRef.current = containerRect;
      hasMoved.current = false;
      isTouchDrag.current = false;

      // 設置拖動狀態
      setDragState({
        isDragging: true,
        operation,
        itemId: item.id,
        itemType: item.itemType,
        startY: e.clientY,
        startX: e.clientX,
        startMouseY: e.clientY - containerRect.top,
        startMouseX: e.clientX - containerRect.left,
        originalStartTime: new Date(item.startTime),
        originalEndTime: new Date(item.endTime!),
        currentStartTime: new Date(item.startTime),
        currentEndTime: new Date(item.endTime!),
        originalDayIndex: dayIndex,
        currentDayIndex: dayIndex,
      });
    },
    [enabled]
  );

  // 拖動中
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState || !containerRectRef.current) return;

      hasMoved.current = true;

      const containerRect = containerRectRef.current;
      const currentMouseY = e.clientY - containerRect.top;
      const currentMouseX = e.clientX - containerRect.left;
      const pixelDeltaY = currentMouseY - dragState.startMouseY;

      // 計算目標日期索引（僅在 move 操作且有日期列時）
      let targetDayIndex = dragState.originalDayIndex;
      let targetDate: Date | undefined;

      if (dragState.operation === 'move' && days && days.length > 0) {
        targetDayIndex = calculateTargetDayIndex(
          currentMouseX,
          containerRect.width,
          timeColumnWidth,
          days.length
        );
        targetDate = days[targetDayIndex];
      }

      // 計算新時間（支援跨日期）
      const { newStartTime, newEndTime } = calculateNewTimesWithDate(
        dragState.operation,
        dragState.originalStartTime,
        dragState.originalEndTime,
        pixelDeltaY,
        hourHeight,
        startHour,
        endHour,
        targetDate
      );

      // 更新狀態
      setDragState((prev) =>
        prev
          ? {
            ...prev,
            currentStartTime: newStartTime,
            currentEndTime: newEndTime,
            currentDayIndex: targetDayIndex,
          }
          : null
      );
    },
    [dragState, hourHeight, startHour, endHour, days, timeColumnWidth]
  );

  // 結束拖動
  const handleMouseUp = useCallback(
    async (e: MouseEvent) => {
      if (!dragState) return;

      // 如果沒有移動過，視為點擊事件，不執行更新
      if (!hasMoved.current) {
        setDragState(null);
        containerRectRef.current = null;
        return;
      }

      // 檢查時間是否有變化
      const hasChanged =
        dragState.currentStartTime.getTime() !== dragState.originalStartTime.getTime() ||
        dragState.currentEndTime.getTime() !== dragState.originalEndTime.getTime();

      if (hasChanged && onReschedule) {
        try {
          await onReschedule({
            itemId: dragState.itemId,
            itemType: dragState.itemType,
            newStartTime: dragState.currentStartTime,
            newEndTime: dragState.currentEndTime,
          });
        } catch (error) {
          logger.error('重新排程失敗:', error);
        }
      }

      // 清理拖拽狀態
      // 注意：上層組件（Calendar.tsx）已實現樂觀更新，會在 onReschedule 呼叫時立即更新 UI
      setDragState(null);
      containerRectRef.current = null;
      hasMoved.current = false;
    },
    [dragState, onReschedule]
  );

  // 開始拖動（觸控）
  const handleTouchStart = useCallback(
    (e: React.TouchEvent, item: ScheduleItem, containerRect: DOMRect, dayIndex?: number) => {
      if (!enabled) return;

      // 只處理單點觸控
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];

      // 重置狀態
      isDragActivated.current = false;
      hasMoved.current = false;
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };

      // 保存待處理的拖動數據
      pendingDragData.current = {
        item,
        containerRect,
        dayIndex,
        touch: { clientX: touch.clientX, clientY: touch.clientY },
      };

      // 標記進入長按等待期間（用於觸發滾動偵測監聽器）
      setIsWaitingForLongPress(true);

      // 啟動長按計時器
      clearLongPressTimer();
      longPressTimer.current = setTimeout(() => {
        setIsWaitingForLongPress(false);
        activateDrag();
      }, LONG_PRESS_DELAY);
    },
    [enabled, activateDrag, clearLongPressTimer, LONG_PRESS_DELAY]
  );

  // 觸控拖動中
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      // 只處理單點觸控
      if (e.touches.length !== 1) return;

      const touch = e.touches[0];

      // 如果還未激活拖動，檢查是否為滾動手勢
      if (!isDragActivated.current && touchStartPos.current) {
        const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);
        const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);

        // 如果垂直移動超過門檻，視為滾動，取消長按
        if (deltaY > SCROLL_CANCEL_THRESHOLD || deltaX > SCROLL_CANCEL_THRESHOLD) {
          clearLongPressTimer();
          touchStartPos.current = null;
          pendingDragData.current = null;
          return;
        }
        // 移動距離未超過門檻，不做任何處理，等待長按或觸摸結束
        return;
      }

      // 以下是已激活拖動的邏輯
      if (!dragState || !containerRectRef.current || !isTouchDrag.current) return;

      // 阻止頁面滾動
      e.preventDefault();

      hasMoved.current = true;

      const containerRect = containerRectRef.current;
      const currentMouseY = touch.clientY - containerRect.top;
      const currentMouseX = touch.clientX - containerRect.left;
      const pixelDeltaY = currentMouseY - dragState.startMouseY;

      // 計算目標日期索引（僅在有日期列時）
      let targetDayIndex = dragState.originalDayIndex;
      let targetDate: Date | undefined;

      if (days && days.length > 0) {
        targetDayIndex = calculateTargetDayIndex(
          currentMouseX,
          containerRect.width,
          timeColumnWidth,
          days.length
        );
        targetDate = days[targetDayIndex];
      }

      // 計算新時間（支援跨日期）
      const { newStartTime, newEndTime } = calculateNewTimesWithDate(
        'move', // 觸控只支援移動
        dragState.originalStartTime,
        dragState.originalEndTime,
        pixelDeltaY,
        hourHeight,
        startHour,
        endHour,
        targetDate
      );

      // 更新狀態
      setDragState((prev) =>
        prev
          ? {
            ...prev,
            currentStartTime: newStartTime,
            currentEndTime: newEndTime,
            currentDayIndex: targetDayIndex,
          }
          : null
      );
    },
    [dragState, hourHeight, startHour, endHour, days, timeColumnWidth, clearLongPressTimer, SCROLL_CANCEL_THRESHOLD]
  );

  // 觸控結束
  const handleTouchEnd = useCallback(
    async (e: TouchEvent) => {
      // 清理長按計時器
      clearLongPressTimer();

      // 處理 touchcancel
      if (e.type === 'touchcancel') {
        setDragState(null);
        setIsLongPressing(false);
        setIsWaitingForLongPress(false);
        containerRectRef.current = null;
        hasMoved.current = false;
        isTouchDrag.current = false;
        isDragActivated.current = false;
        touchStartPos.current = null;
        pendingDragData.current = null;
        return;
      }

      // 如果未激活拖動，視為點擊事件
      if (!isDragActivated.current) {
        setIsWaitingForLongPress(false);
        touchStartPos.current = null;
        pendingDragData.current = null;
        // 不阻止事件傳播，讓點擊事件正常觸發
        return;
      }

      // 以下是已激活拖動的邏輯
      if (!dragState || !isTouchDrag.current) return;

      // 如果沒有移動過，也視為點擊（雖然已長按，但未拖動）
      if (!hasMoved.current) {
        setDragState(null);
        setIsLongPressing(false);
        setIsWaitingForLongPress(false);
        containerRectRef.current = null;
        isTouchDrag.current = false;
        isDragActivated.current = false;
        touchStartPos.current = null;
        pendingDragData.current = null;
        return;
      }

      // 檢查時間是否有變化
      const hasChanged =
        dragState.currentStartTime.getTime() !== dragState.originalStartTime.getTime() ||
        dragState.currentEndTime.getTime() !== dragState.originalEndTime.getTime();

      // 立即清理拖拽狀態，避免 Promise 等待期間 UI 卡在預覽狀態
      // 注意：這會導致卡片短暫跳回原位，然後由上層組件的樂觀更新接管（Calendar.tsx 已實現）
      // 理想情況下應該延遲清除 dragState 直到樂觀更新完成，以避免視覺閃爍
      const finalDragState = { ...dragState }; // 複製一份用於調用

      // 清理拖拽狀態
      setDragState(null);
      setIsLongPressing(false);
      setIsWaitingForLongPress(false);
      containerRectRef.current = null;
      hasMoved.current = false;
      isTouchDrag.current = false;
      isDragActivated.current = false;
      touchStartPos.current = null;
      pendingDragData.current = null;

      if (hasChanged && onReschedule) {
        try {
          await onReschedule({
            itemId: finalDragState.itemId,
            itemType: finalDragState.itemType,
            newStartTime: finalDragState.currentStartTime,
            newEndTime: finalDragState.currentEndTime,
          });
        } catch (error) {
          logger.error('重新排程失敗', error);
          // 錯誤處理由上層組件（Calendar.tsx）負責
        }
      }
    },
    [dragState, onReschedule, clearLongPressTimer]
  );

  // 獲取拖動預覽樣式
  const getDragPreviewStyle = useCallback(
    (item: ScheduleItem, dayIndex?: number): React.CSSProperties | null => {
      if (!dragState || dragState.itemId !== item.id) {
        return null;
      }

      // 如果在週視圖且日期不匹配，不顯示預覽
      if (dayIndex !== undefined && dragState.currentDayIndex !== dayIndex) {
        return null;
      }

      // 計算新的位置
      const startTimeHour = dragState.currentStartTime.getHours();
      const startTimeMinute = dragState.currentStartTime.getMinutes();
      const endTimeHour = dragState.currentEndTime.getHours();
      const endTimeMinute = dragState.currentEndTime.getMinutes();

      const top =
        (startTimeHour - startHour) * hourHeight +
        (startTimeMinute / 60) * hourHeight;
      const height =
        (endTimeHour - startTimeHour) * hourHeight +
        ((endTimeMinute - startTimeMinute) / 60) * hourHeight;

      return {
        top: `${top}px`,
        height: `${Math.max(height, DISPLAY_CONFIG.MIN_TIMEBOX_HEIGHT)}px`,
        opacity: 0.8,
        zIndex: 100,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      };
    },
    [dragState, hourHeight, startHour]
  );

  // 獲取游標樣式
  const getCursorStyle = useCallback(
    (mouseY: number, elementHeight: number): string => {
      if (!enabled) return 'pointer';
      const operation = getDragOperation(mouseY, elementHeight);
      return getDragCursor(operation);
    },
    [enabled]
  );

  // 獲取當前拖拽的目標日期索引
  const getCurrentDayIndex = useCallback((): number | undefined => {
    return dragState?.currentDayIndex;
  }, [dragState]);

  // 重置拖動狀態
  const resetDragState = useCallback(() => {
    clearLongPressTimer();
    setDragState(null);
    setIsLongPressing(false);
    setIsWaitingForLongPress(false);
    containerRectRef.current = null;
    hasMoved.current = false;
    isDragActivated.current = false;
    touchStartPos.current = null;
    pendingDragData.current = null;
  }, [clearLongPressTimer]);

  // 長按等待期間的滾動偵測
  // 問題：dragState 在長按激活前是 null，所以拖動事件監聽器不會被註冊
  // 這會導致滾動時無法取消長按計時器
  const handleScrollDuringLongPress = useCallback(
    (e: TouchEvent) => {
      // 只在長按等待期間（未激活）處理
      if (isDragActivated.current || !touchStartPos.current) return;

      if (e.touches.length !== 1) return;
      const touch = e.touches[0];

      const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);
      const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);

      // X 或 Y 移動超過門檻，視為滾動（上下滾動或左右切換日期），取消長按
      if (deltaY > SCROLL_CANCEL_THRESHOLD || deltaX > SCROLL_CANCEL_THRESHOLD) {
        clearLongPressTimer();
        setIsWaitingForLongPress(false);
        touchStartPos.current = null;
        pendingDragData.current = null;
      }
    },
    [clearLongPressTimer, SCROLL_CANCEL_THRESHOLD]
  );

  // 長按等待期間監聽滾動（取消長按）
  useEffect(() => {
    if (isWaitingForLongPress) {
      document.addEventListener('touchmove', handleScrollDuringLongPress, { passive: true });
      return () => {
        document.removeEventListener('touchmove', handleScrollDuringLongPress);
      };
    }
  }, [isWaitingForLongPress, handleScrollDuringLongPress]);

  // 全局事件監聽
  useEffect(() => {
    if (dragState) {
      // 滑鼠事件監聽
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // 觸控事件監聽（使用 passive: false 以允許 preventDefault）
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchcancel', handleTouchEnd);

      // 添加全局游標樣式（僅滑鼠）
      if (!isTouchDrag.current) {
        document.body.style.cursor = getDragCursor(dragState.operation);
      }
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [dragState, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return {
    dragState,
    isDragging: !!dragState,
    isLongPressing,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    getDragPreviewStyle,
    getCursorStyle,
    getCurrentDayIndex,
    resetDragState,
  };
};

export default useCalendarDragDrop;
